// src/components/settings/NotificationPreferencesMatrix.tsx
// Matrice signal × canale con switch. Default = abilitato lato backend,
// quindi se non riceviamo la riga rendiamo lo switch "on".
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Switch } from 'components/ui/switch';
import {
  listPreferences,
  updatePreferencesBulk,
  type ChannelPreference,
  type NotificationChannel,
} from 'services/notificationPreferences';

interface SignalSpec {
  key: string;       // signal_function_name lato backend
  label: string;     // testo UI
}

// Lista dei "tipi" di notifica che esponiamo in UI. Si aggiunge quando
// il backend introduce nuovi `signal_function_name`.
const SIGNALS: SignalSpec[] = [
  { key: 'task_assigned',       label: 'Task assegnati' },
  { key: 'task_updated',        label: 'Aggiornamenti task' },
  { key: 'appointment_reminder', label: 'Promemoria appuntamenti' },
  { key: 'event_reminder',      label: 'Promemoria eventi' },
  { key: 'meeting_reminder',    label: 'Promemoria meeting' },
  { key: 'expiring_task',       label: 'Task in scadenza' },
];

const CHANNELS: { key: NotificationChannel; label: string }[] = [
  { key: 'webpush', label: 'Push' },
  { key: 'email', label: 'Email' },
  { key: 'telegram', label: 'Telegram' },
];

function buildKey(channel: NotificationChannel, signal: string) {
  return `${channel}::${signal}`;
}

export function NotificationPreferencesMatrix() {
  // Mappa locale: chiave `channel::signal` → enabled
  const [state, setState] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Stato iniziale: tutto abilitato (= default backend).
  useEffect(() => {
    const initial: Record<string, boolean> = {};
    for (const c of CHANNELS) for (const s of SIGNALS) initial[buildKey(c.key, s.key)] = true;
    setState(initial);
  }, []);

  // Carica preferenze esplicite dal backend
  useEffect(() => {
    listPreferences()
      .then((prefs) => {
        setState((prev) => {
          const next = { ...prev };
          for (const p of prefs) {
            next[buildKey(p.channel, p.signal_function_name)] = p.enabled;
          }
          return next;
        });
      })
      .catch((err) => console.warn('[NotificationPreferencesMatrix] load fail:', err))
      .finally(() => setLoaded(true));
  }, []);

  const handleToggle = useCallback(
    async (channel: NotificationChannel, signal: string, enabled: boolean) => {
      const key = buildKey(channel, signal);
      setState((prev) => ({ ...prev, [key]: enabled }));
      setSaving(true);
      try {
        await updatePreferencesBulk([
          { channel, signal_function_name: signal, enabled },
        ]);
      } catch (err) {
        // Rollback ottimistico
        console.warn('[NotificationPreferencesMatrix] save fail:', err);
        setState((prev) => ({ ...prev, [key]: !enabled }));
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const rows = useMemo(() => SIGNALS, []);

  return (
    <div className="space-y-2">
      <h5 className="text-[var(--hexa-text-secondary)] text-[10px] font-medium uppercase tracking-wider">
        Cosa notificare e dove
      </h5>
      <div className="rounded-lg border border-[rgba(106,77,255,0.25)] bg-[var(--hexa-surface)] overflow-hidden">
        <table className="w-full text-[10px]">
          <thead className="bg-[var(--hexa-surface-hover)]">
            <tr>
              <th className="text-left px-3 py-2 text-[var(--hexa-text-secondary)] font-medium">Evento</th>
              {CHANNELS.map((c) => (
                <th
                  key={c.key}
                  className="text-center px-2 py-2 text-[var(--hexa-text-secondary)] font-medium"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.key} className="border-t border-[rgba(106,77,255,0.15)]">
                <td className="px-3 py-2 text-[var(--hexa-text-primary)]">{s.label}</td>
                {CHANNELS.map((c) => {
                  const key = buildKey(c.key, s.key);
                  const checked = state[key] ?? true;
                  return (
                    <td key={c.key} className="px-2 py-2 text-center">
                      <Switch
                        checked={checked}
                        disabled={!loaded || saving}
                        onCheckedChange={(v) => handleToggle(c.key, s.key, v)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[var(--hexa-text-secondary)] text-[9px] italic">
        Le notifiche in-app (icona del campanello) sono sempre attive.
      </p>
    </div>
  );
}
